<script setup>
/**
 * The lesson card (community-lesson DESIGN.md L1; redone 2026-10-28,
 * lesson-redo DESIGN.md B, H1, I): ONE component for the Community feed and
 * the share sheet's preview, so the preview really is what strangers will see.
 *
 * A two-line story with the lesson as the star:
 * - the picture at 3:2 (16:10 on a phone), or, when it can't be used, the
 *   four cards fanned on the plum table. On it (Chris, 2026-11-02): the
 *   sharer's chip bottom-left (round avatar + name on a dark scrim, the link
 *   to their profile; "A seeker" with a neutral icon, never a link) and the
 *   Mystic's square mark bottom-right (its tap opens the Mystic's page, never
 *   the lesson); the expand mark stays top-right;
 * - the lead-in: *Someone asked:* “the asker's own question” (2 lines held);
 * - the lesson as the headline, italic (3 lines held, so a row lines up);
 * - the foot: only "✦ Spoke to me · 12", centred, a bordered 44px pill (a
 *   toggle in place).
 *
 * `preview` (the share sheet before publishing): the response unlit, no count,
 * no mark to tap. `wide`: full width. A tap anywhere else emits `open`.
 *
 * The picture opens (picture-caption rev 1, 2026-10-30): on the feed and the
 * profile tiles it is its own button (PictureOpen, its tap stopped), full size
 * with the lesson's caption over its foot. The card's one line stays the
 * lesson; the rest of the card still opens the lesson.
 *
 * The share sheet (rev 13, DESIGN.md §E5): `look` tells the switch's story,
 * `veiled` while off (the picture desaturated under a cream veil, the words at
 * 62%, no mark) and `clear` while on; either centres the lead-in and the lesson.
 * `editable` adds a "✎ Edit" chip at the lesson's corner and makes the lesson
 * itself a tap into edit (both emit `edit`); `editing` swaps the lesson for the
 * `edit` slot (the sheet's field, Cancel · Done) at full strength; `updated`
 * glows the card with "✦ Updated · the question and the card lines follow it".
 */
import { computed, ref, watch } from 'vue'
import {
  captionOf, creditAvatar, creditLine, creditName, creditSeeker, openCreditSeeker, hasPicture, lessonCards, leadIn, mysticOf, spokeFrom, SPOKE_SHORT,
} from '@/utils/lesson'
import { toggleReadingSupport } from '@/api-service'
import { track } from '@/utils/analytics'
import MysticMark from './MysticMark.vue'
import PictureOpen from './PictureOpen.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  lesson: { type: Object, required: true },
  preview: { type: Boolean, default: false },
  wide: { type: Boolean, default: false },
  // The feed's card is a button; the share sheet's is not.
  interactive: { type: Boolean, default: false },
  look: { type: String, default: '' },          // '' | 'veiled' | 'clear' (the share sheet)
  editable: { type: Boolean, default: false },
  editing: { type: Boolean, default: false },
  updated: { type: Boolean, default: false },
})
const emit = defineEmits(['open', 'edit'])

const picFailed = ref(false)
watch(() => props.lesson && props.lesson.image_url, () => { picFailed.value = false })
const showPic = computed(() => hasPicture(props.lesson) && !picFailed.value)
const cards = computed(() => lessonCards(props.lesson).slice(0, 5))
const failedArt = ref({})
// "Spoke to me" answers in place on the feed (Chris, 2026-10-27): a toggle, its
// tap its own, never the card's open. The share sheet's preview stays plain text.
const spoke = ref(!!(props.lesson && (props.lesson.spoke ?? props.lesson.supported)))
const count = ref(Number(props.lesson && (props.lesson.spoke_count ?? props.lesson.support_count)) || 0)
watch(() => props.lesson, (l) => {
  spoke.value = !!(l && (l.spoke ?? l.supported))
  count.value = Number(l && (l.spoke_count ?? l.support_count)) || 0
})
const canSpeak = computed(() => props.interactive && !props.preview)
const busy = ref(false)
async function toggleSpoke() {
  if (busy.value) return
  busy.value = true
  try {
    const r = spokeFrom(await toggleReadingSupport(props.lesson.short_uuid))
    if (r) {
      spoke.value = r.spoke
      count.value = r.count
      if (r.spoke) track('lesson_resonated', { item_id: props.lesson.short_uuid })
    }
  } finally { busy.value = false }
}
const lead = computed(() => leadIn(props.lesson))
const star = computed(() => String(props.lesson.lesson || '').trim())
const mystic = computed(() => mysticOf(props.lesson))
const credit = computed(() => creditLine(props.lesson))
// A named credit is a link to that seeker's profile (never in the share
// sheet's preview, never for "a seeker"); its tap is its own, not the card's.
const creditLink = computed(() => props.interactive && !props.preview && !!creditSeeker(props.lesson))
const who = computed(() => creditName(props.lesson))
// The sharer's avatar: only beside a named credit (credit_seeker), never "a seeker".
const named = computed(() => !!creditSeeker(props.lesson))
const avatar = computed(() => creditAvatar(props.lesson))
const avFailed = ref(false)
watch(avatar, () => { avFailed.value = false })
const initial = computed(() => (who.value || '✦').trim().charAt(0).toUpperCase())
function open() { if (props.interactive) emit('open', props.lesson) }
const veiled = computed(() => props.look === 'veiled')
const showMark = computed(() => !props.preview && !veiled.value)
// The picture opens on the feed and the profile tiles, never in the share sheet.
const picOpens = computed(() => showPic.value && props.interactive && !props.preview && !props.look)
const caption = computed(() => captionOf(props.lesson))
function edit() { if (props.editable && !props.editing) emit('edit') }
function onEditKey(e) {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); edit() }
}
function onKey(e) {
  if (!props.interactive || e.target !== e.currentTarget) return
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
}
</script>

<template>
  <article class="lc" :class="{ wide, tap: interactive, sheet: !!look, veiled, clear: look === 'clear', editable, editing, updated }" data-testid="lesson-card"
           :role="interactive ? 'link' : null" :tabindex="interactive ? 0 : null"
           :aria-label="interactive ? (star || lead.text) : null" @click="open" @keydown="onKey">
    <div class="lc-pic" :class="{ fan: !showPic }" :data-testid="showPic ? null : 'lesson-card-cards-lead'">
      <PictureOpen v-if="picOpens" class="lc-open" data-testid="lesson-card-picture-open" :src="lesson.image_url" :caption="caption"
                   img-testid="lesson-card-picture" @error="picFailed = true" />
      <img v-else-if="showPic" :src="lesson.image_url" alt="" class="lc-img" data-testid="lesson-card-picture" @error="picFailed = true" />
      <template v-else>
        <span v-for="(c, i) in cards" :key="i" class="c" aria-hidden="true">
          <img v-if="c.image_url && !failedArt[i]" :src="c.image_url" alt="" :class="{ rev: c.reversed }"
               @error="failedArt = { ...failedArt, [i]: true }" />
        </span>
      </template>
      <!-- The sharer (2026-11-02): bottom-left, its tap its own. Screen readers hear "Shared by …". -->
      <button v-if="creditLink" type="button" class="lc-chipbtn" data-testid="lesson-card-credit-link" :aria-label="credit"
              @click.stop="openCreditSeeker(lesson)" @keydown.enter.stop @keydown.space.stop>
        <span class="lc-chip link">
          <span class="lc-av" data-testid="lesson-card-avatar" aria-hidden="true">
            <img v-if="avatar && !avFailed" :src="avatar" alt="" @error="avFailed = true" />
            <span v-else class="lc-av-ph">{{ initial }}</span>
          </span>
          <span class="lc-by" data-testid="lesson-card-credit"><span class="lc-sr">Shared by </span><span class="lc-nm">{{ who }}</span></span>
        </span>
      </button>
      <span v-else class="lc-chip" :class="{ anon: who === 'a seeker' }">
        <span v-if="named" class="lc-av" data-testid="lesson-card-avatar" aria-hidden="true">
          <img v-if="avatar && !avFailed" :src="avatar" alt="" @error="avFailed = true" />
          <span v-else class="lc-av-ph">{{ initial }}</span>
        </span>
        <span v-else class="lc-av lc-av-anon" data-testid="lesson-card-avatar-anon" aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
            <circle cx="8" cy="5.8" r="2.6" /><path d="M3.4 13.4c.7-2.4 2.5-3.7 4.6-3.7s3.9 1.3 4.6 3.7" />
          </svg>
        </span>
        <span class="lc-by" data-testid="lesson-card-credit"><span class="lc-sr">Shared by </span><span class="lc-nm">{{ who }}</span></span>
      </span>
      <MysticMark v-if="showMark" :mystic="mystic" side="right" />
    </div>
    <div class="lc-body">
      <p class="lc-li" data-testid="lesson-card-lead"><i v-if="lead.key" class="li-k">{{ lead.key }}</i>{{ lead.key ? ' ' : '' }}{{ lead.text }}</p>
      <slot v-if="editing" name="edit" />
      <div v-else-if="editable" class="lc-lsw">
        <h3 class="lc-ls lc-ls-tap" data-testid="lesson-card-lesson" role="button" tabindex="0"
            :aria-label="`Edit the lesson: ${star}`" @click.stop="edit" @keydown="onEditKey">{{ star }}</h3>
        <button type="button" class="lc-edit" data-testid="lesson-card-edit" @click.stop="edit"><span class="lc-edit-c">✎ Edit</span></button>
      </div>
      <h3 v-else class="lc-ls" data-testid="lesson-card-lesson">{{ star }}</h3>
      <div class="lc-foot">
        <button v-if="canSpeak" type="button" class="lc-res lc-res-btn" :class="{ on: spoke }" :aria-pressed="spoke ? 'true' : 'false'"
                :disabled="busy" data-testid="lesson-card-response"
                @click.stop="toggleSpoke" @keydown.enter.stop @keydown.space.stop><span class="st">✦</span> {{ SPOKE_SHORT }}<span v-if="count" class="n"> · {{ count }}</span></button>
        <span v-else class="lc-res" data-testid="lesson-card-response">✦ {{ SPOKE_SHORT }}<template v-if="count && !preview"> · {{ count }}</template></span>
      </div>
    </div>
    <span v-if="updated && !editing" class="lc-upd" data-testid="lesson-card-updated">✦ Updated · the question and the card lines follow it</span>
  </article>
</template>

<style scoped>
.lc {
  --cream: #f4ecdc; --plum: #2a1845; --gold: #c9a94e; --golddk: #8a6a2a; --ink: #2a1845; --ink2: #3d2f55; --muted: #7a6a8f; --amethyst: #4e2776;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  position: relative; box-sizing: border-box; width: 300px; max-width: 100%; flex: 0 0 auto; border-radius: 18px; overflow: hidden;
  display: flex; flex-direction: column; background: var(--cream); color: var(--ink); text-align: left;
  box-shadow: 0 24px 46px -26px rgba(0, 0, 0, .95); border: 1px solid rgba(231, 201, 131, .22);
  transition: transform .25s ease, box-shadow .25s ease;
}
.lc *, .lc *::before, .lc *::after { box-sizing: border-box; }
.lc img { display: block; }
.lc.wide { width: 100%; }
.lc.tap { cursor: pointer; }
.lc.tap:hover { transform: translateY(-2px); box-shadow: 0 30px 50px -24px rgba(0, 0, 0, 1); }
.lc.tap:focus-visible { outline: 2px solid #e7c983; outline-offset: 3px; }
/* The picture at 3:2; the card's hover leans it in (the mark's name slides out on its own hover only, clear of the sharer's chip). */
.lc-pic { position: relative; flex: none; aspect-ratio: 3 / 2; background: var(--plum); overflow: hidden; }
.lc-img { width: 100%; height: 100%; object-fit: cover; transition: transform .8s cubic-bezier(.2, .8, .2, 1); }
.lc.tap:hover .lc-img { transform: scale(1.04); }
.lc-pic .lc-open { position: absolute; inset: 0; }
.lc-open :deep(.po-img) { transition: transform .8s cubic-bezier(.2, .8, .2, 1); }
.lc.tap:hover .lc-open :deep(.po-img) { transform: scale(1.04); }
/* No picture: the four cards fanned on the plum table. */
.lc-pic.fan { display: flex; align-items: center; justify-content: center; --cw: 25%; --ov: -.1;
  background: radial-gradient(70% 90% at 50% 55%, #4b2a7a 0%, #2a1548 55%, #160a2b 100%); }
.lc-pic.fan::after { content: ''; position: absolute; left: 12%; right: 12%; bottom: 14%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(231, 201, 131, .35), transparent); }
.lc-pic.fan .c {
  position: relative; z-index: 1; flex: none; width: var(--cw); aspect-ratio: 2 / 3; border-radius: 6px; overflow: hidden;
  border: 1px solid rgba(231, 201, 131, .85); background: var(--plum); box-shadow: 0 14px 22px -10px rgba(0, 0, 0, .9);
  margin: 0 calc(var(--cw) * var(--ov)); transform: translateY(var(--y, 0)) rotate(var(--r, 0deg));
  transition: transform .45s cubic-bezier(.2, .8, .2, 1), margin .45s cubic-bezier(.2, .8, .2, 1);
}
.lc.tap:hover .lc-pic.fan { --ov: .02; }
.lc-pic.fan .c img { width: 100%; height: 100%; object-fit: cover; }
.lc img.rev { transform: rotate(180deg); }
.lc-pic.fan .c:nth-child(1) { --r: -8deg; --y: 7%; } .lc-pic.fan .c:nth-child(2) { --r: -2.5deg; }
.lc-pic.fan .c:nth-child(3) { --r: 2.5deg; } .lc-pic.fan .c:nth-child(4) { --r: 8deg; --y: 7%; }
.lc-pic.fan .c:nth-child(5) { --r: 12deg; --y: 14%; }

.lc-body { flex: 1; display: flex; flex-direction: column; min-height: 0; padding: 14px 18px 4px; }
/* The lead-in: two lines held, so a row lines up. */
.lc-li { margin: 0 0 8px; font: 500 16px/1.35 var(--serif); color: var(--ink2); overflow-wrap: anywhere;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; overflow: hidden; min-height: calc(2 * 1.35em); }
.li-k { font-style: italic; color: var(--muted); margin-right: .2em; }
/* The lesson, the star: three lines held. */
.lc-ls { margin: 0; font: italic 600 25px/1.13 var(--serif); color: var(--ink); text-wrap: balance; letter-spacing: -.004em; overflow-wrap: anywhere;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; line-clamp: 3; overflow: hidden; min-height: calc(3 * 1.13em); }
/* The foot (2026-11-02): only "✦ Spoke to me · N", centred, a pill that reads as a button. */
.lc-foot { display: flex; align-items: center; justify-content: center; margin-top: auto; padding: 10px 0 10px; flex: none; min-width: 0;
  font: 500 12px var(--ui); color: var(--muted); }
.lc-res { flex: none; white-space: nowrap; color: var(--amethyst); font: 600 13px var(--ui); }
.lc-res-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 44px; min-width: 44px; max-width: 100%; padding: 0 20px;
  border: 1px solid rgba(78, 39, 118, .32); border-radius: 22px; background: #fffaf0; cursor: pointer; font: 600 14px var(--ui);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .8), 0 6px 14px -10px rgba(42, 24, 69, .7);
  transition: background .2s, border-color .2s, box-shadow .2s, transform .12s ease;
}
.lc-res-btn .st { display: inline-block; transition: transform .5s cubic-bezier(.2, .8, .2, 1), color .2s; }
.lc-res-btn .n { font-weight: 500; color: var(--muted); }
.lc-res-btn:hover:not(:disabled) { background: #fff; border-color: rgba(78, 39, 118, .6); box-shadow: inset 0 1px 0 #fff, 0 8px 18px -10px rgba(42, 24, 69, .8); }
.lc-res-btn:active:not(:disabled) { transform: scale(.97); background: #f3e8d2; box-shadow: inset 0 1px 3px rgba(42, 24, 69, .18); }
.lc-res-btn.on { color: var(--golddk); border-color: rgba(201, 169, 78, .85); background: #fbf1da; }
.lc-res-btn.on .st { transform: rotate(144deg) scale(1.25); color: var(--gold); }
.lc-res-btn:disabled { cursor: default; }
.lc-res-btn:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
/* On the picture (2026-11-02): the sharer's chip bottom-left, on a dark scrim
   so it reads over any picture; it stops short of the Mystic's mark
   (bottom-right), and a long name ends in "…". 34px seen, 44px to hit. */
.lc-chip {
  position: absolute; left: 10px; bottom: 10px; z-index: 3; display: inline-flex; align-items: center; gap: 7px;
  max-width: calc(100% - 66px); height: 34px; padding: 3px 12px 3px 3px; margin: 0; border: 0; border-radius: 17px;
  background: rgba(20, 9, 38, .62); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  box-shadow: 0 0 0 1px rgba(231, 201, 131, .45); color: #fff3dc; font: inherit; text-align: left;
}
/* The link: a 44px button holding the 34px chip (5px clear all round). */
.lc-chipbtn {
  position: absolute; left: 5px; bottom: 5px; z-index: 3; display: flex; align-items: center; min-height: 44px; min-width: 44px;
  max-width: calc(100% - 56px); padding: 5px; margin: 0; border: 0; background: none; cursor: pointer; font: inherit; border-radius: 22px;
}
.lc-chipbtn .lc-chip { position: relative; left: auto; bottom: auto; max-width: 100%; }
.lc-chip.link { transition: background .2s, box-shadow .2s; }
.lc-chipbtn:hover .lc-chip { background: rgba(20, 9, 38, .82); box-shadow: 0 0 0 1px rgba(231, 201, 131, .9); }
.lc-chipbtn:active .lc-chip { background: rgba(20, 9, 38, .9); }
.lc-chipbtn:focus-visible { outline: none; }
.lc-chipbtn:focus-visible .lc-chip { outline: 2px solid #e7c983; outline-offset: 2px; }
.lc-av { width: 28px; height: 28px; border-radius: 50%; overflow: hidden; flex: none; background: var(--plum); border: 1px solid rgba(231, 201, 131, .8); }
.lc-av img { width: 100%; height: 100%; object-fit: cover; }
.lc-av-ph { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; font: 600 12px/1 var(--display); color: var(--gold); }
.lc-av-anon { display: grid; place-items: center; color: #e7c983; }
.lc-av-anon svg { width: 16px; height: 16px; }
.lc-by { display: flex; align-items: center; min-width: 0; white-space: nowrap; }
.lc-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.lc-nm { display: block; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font: 600 12.5px/1.25 var(--ui); color: #fff3dc; }
.lc-chip.link .lc-nm { text-decoration: underline; text-decoration-color: rgba(255, 233, 184, .45); text-underline-offset: 3px; }
.lc-chipbtn:hover .lc-nm { text-decoration-color: currentColor; }
.lc-chip.anon .lc-nm::first-letter { text-transform: uppercase; }
/* The editable sheet card: the Edit chip sits on the seam, so the picture's chips step up. */
.lc.editable .lc-pic > .lc-chip, .lc.editable :deep(.mk) { bottom: 22px; }
.lc.editable .lc-chipbtn { bottom: 17px; }
/* The share sheet (rev 13): the lesson centred; the look tells the switch's story. */
.lc.sheet .lc-li, .lc.sheet .lc-ls { text-align: center; }
.lc.sheet .lc-pic::after { content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0; transition: opacity .6s ease;
  background: linear-gradient(180deg, rgba(244, 236, 220, .55), rgba(244, 236, 220, .35)); }
.lc.sheet .lc-img, .lc.sheet .lc-pic.fan .c { transition: filter .6s ease; }
.lc.sheet .lc-body { position: relative; }
.lc.sheet .lc-li, .lc.sheet .lc-ls, .lc.sheet .lc-foot { transition: opacity .6s ease; }
.lc.veiled .lc-pic::after { opacity: 1; }
.lc.veiled .lc-img, .lc.veiled .lc-pic.fan .c { filter: grayscale(.65) brightness(.9); }
/* The words at 62% (the Edit chip stays clear); full strength while editing. */
.lc.veiled .lc-li, .lc.veiled .lc-ls, .lc.veiled .lc-foot { opacity: .62; }
.lc.veiled.editing .lc-li, .lc.veiled.editing .lc-foot { opacity: 1; }
/* The lesson is visibly editable: a tap on it, or the chip at its corner. */
.lc-lsw { position: static; }
.lc-ls-tap { cursor: text; border-radius: 6px; }
.lc-ls-tap:hover { background: rgba(201, 169, 78, .1); }
.lc-ls-tap:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
/* A 44px tap target holding a small chip, on the seam between the picture and the words (never over them). */
.lc-edit {
  position: absolute; top: -22px; right: 6px; z-index: 1; display: grid; place-items: center; min-width: 44px; height: 44px; padding: 0 6px;
  border: 0; background: none; cursor: pointer; border-radius: 22px;
}
.lc-edit-c {
  display: block; height: 26px; padding: 0 10px; border-radius: 13px; border: 1px solid rgba(201, 169, 78, .7); background: #fffaf0; color: var(--golddk);
  font: 600 11.5px/24px var(--ui); letter-spacing: .02em; white-space: nowrap; box-shadow: 0 4px 10px -6px rgba(42, 24, 69, .5);
}
.lc-edit:hover .lc-edit-c { border-color: var(--gold); background: #fff; }
.lc-edit:focus-visible { outline: none; }
.lc-edit:focus-visible .lc-edit-c { outline: 2px solid var(--amethyst); outline-offset: 2px; }
/* Updated in place. */
.lc.updated { box-shadow: 0 0 0 2px rgba(231, 201, 131, .9), 0 0 24px rgba(231, 201, 131, .35), 0 24px 46px -26px rgba(0, 0, 0, .95); }
.lc.updated .lc-ls { animation: lc-new .9s ease both; }
@keyframes lc-new { from { opacity: .2; } }
.lc-upd { display: block; padding: 0 18px 12px; text-align: center; font: 600 11.5px var(--ui); color: var(--golddk); }
@media (max-width: 767px) {
  .lc-pic { aspect-ratio: 16 / 10; }
  .lc-pic.fan { --cw: 21%; }
  .lc-ls { font-size: 26px; min-height: 0; }
  .lc-li { min-height: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .lc, .lc-img, .lc-pic.fan .c, .lc-res-btn, .lc-res-btn .st, .lc-chip { transition: none; }
  .lc-res-btn:active:not(:disabled) { transform: none; }
  .lc.sheet .lc-pic::after, .lc.sheet .lc-img, .lc.sheet .lc-pic.fan .c, .lc.sheet .lc-li, .lc.sheet .lc-ls, .lc.sheet .lc-foot { transition: none; }
  .lc.updated .lc-ls { animation: none; }
  .lc.tap:hover { transform: none; }
  .lc.tap:hover .lc-img, .lc.tap:hover .lc-open :deep(.po-img) { transform: none; }
}
</style>
